// ===== AuthContext.js CORREGIDO =====
// src/context/AuthContext.js

import React, { createContext, useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { authService, deliveryService } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useBrand } from './BrandContext';

const AuthContext = createContext(null);

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [pendingCount, setPendingCount] = useState(0);
    const navigate = useNavigate();
    const { setBrandKey } = useBrand();

    // Cuando el backend nos dice qué empresa está activa en la sesión, sincronizamos
    // el BrandContext para que la UI (sider, header, login) refleje esa marca,
    // incluso después de un F5 si el localStorage de marca estaba desincronizado.
    useEffect(() => {
        if (user?.activeCompany) {
            setBrandKey(user.activeCompany);
        }
    }, [user, setBrandKey]);

    const fetchPendingCount = useCallback(async () => {
        if (user?.role?.profile?.permissions?.pages?.viewDeletionManagement) {
            try {
                const response = await deliveryService.getPendingDeletionRequestsCount();
                if (response.data.success) {
                    setPendingCount(response.data.data.count);
                }
            } catch (error) {
                console.error("Error al obtener el contador de notificaciones:", error);
                setPendingCount(0); 
            }
        } else {
            setPendingCount(0); 
        }
    }, [user]);

    useEffect(() => {
        const pollingIntervalSeconds = user?.notificationPollingIntervalSeconds;
        
        if (!user || !user.role?.profile?.permissions?.pages?.viewDeletionManagement || !pollingIntervalSeconds || pollingIntervalSeconds <= 0) {
            setPendingCount(0);
            return; 
        }

        fetchPendingCount(); 
        const intervalMilliseconds = pollingIntervalSeconds * 1000;        
        const intervalId = setInterval(fetchPendingCount, intervalMilliseconds);

        return () => {
            clearInterval(intervalId);
        };
    }, [user, fetchPendingCount]); 
    
    // CORRECCIÓN: Solo usar '/login' sin agregar /optimus
    const logout = useCallback(async (reason) => {
        // Guardar la razón del logout si existe
        if (reason) {
            sessionStorage.setItem('logoutReason', reason);
        }
        
        // Llamar al servicio de logout
        await authService.logout();
        
        // Limpiar el estado del usuario
        setUser(null);
        
        // (basename del Router quitado tras mover al dominio dedicado optimus.twparts.mx)
        navigate('/login', { replace: true });
    }, [navigate]);

    useEffect(() => {
        const initializeAuth = async () => {
            const token = sessionStorage.getItem('accessToken');
            if (token) {
                try {
                    const { data: userData } = await authService.getMe();
                    setUser(userData.data);
                } catch (error) {
                    console.error("Fallo al obtener datos del usuario, limpiando sesión.", error);
                    await logout(); 
                }
            }
            setLoading(false);
        };
        initializeAuth();
    }, [logout]); 

    const login = useCallback(async (username, password, companyKey) => {
        try {
            await authService.login(username, password, companyKey);
            const { data: userData } = await authService.getMe();
            setUser(userData.data);
            return true;
        } catch (error) {
            console.error('Error de login en AuthContext:', error);
            throw error;
        }
    }, []);

    const value = useMemo(() => ({
        user,
        loading,
        login,
        logout,
        pendingCount,
        fetchPendingCount,
    }), [user, loading, login, logout, pendingCount, fetchPendingCount]);

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};